import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Scale, User, Phone, Mail, MapPin, Briefcase, Tag, Calendar, Clock,
  FileText, MessageSquare, Lock, EyeOff, ShieldCheck, Shield, AlertTriangle,
  Sparkles, Target, RefreshCw, Paperclip
} from "lucide-react";
import LegalAnalysisDisplay from "@/components/admin/LegalAnalysisDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); }
  catch { return ts; }
}

const TABS = [
  { id: "info", label: "Overview" },
  { id: "scoring", label: "Case Score" },
  { id: "legal", label: "Legal Analysis" },
  { id: "transcript", label: "Chat Transcript" },
  { id: "documents", label: "Documents" },
];

// Privacy curtain — sits on top of the contact area so even DOM inspection
// won't recover the contact data (which the backend has already redacted out).
function PrivacyCurtain({ children, label = "Contact info hidden" }) {
  return (
    <div className="relative rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] overflow-hidden" data-testid="privacy-curtain">
      <div className="pointer-events-none select-none filter blur-md opacity-30 p-5">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 py-6 backdrop-blur-md bg-card/80">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-3">
          <Lock className="w-5 h-5 text-amber-400" />
        </div>
        <p className="text-sm font-semibold text-amber-300">{label}</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm">
          The person who shared this link chose to keep the client&apos;s name, phone and email private.
        </p>
      </div>
    </div>
  );
}

export default function SharedLeadView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/share/lead/${token}`);
        setData(res.data);
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (status === 410) setError({ type: "expired", message: detail || "This share link has expired." });
        else if (status === 404) setError({ type: "missing", message: detail || "This share link is invalid or has been revoked." });
        else setError({ type: "error", message: detail || "Could not load this shared lead." });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background" data-testid="share-loading">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-4" data-testid="share-error-page">
        <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card/40 p-8 shadow-xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-destructive/15 border border-destructive/30 flex items-center justify-center mb-4">
            {error.type === "expired" ? <Clock className="w-6 h-6 text-destructive" /> : <AlertTriangle className="w-6 h-6 text-destructive" />}
          </div>
          <h1 className="text-lg font-semibold mb-1">
            {error.type === "expired" ? "Link expired" : "Link not available"}
          </h1>
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <p className="text-xs text-muted-foreground/70 mt-4">Ask the person who shared this link to generate a new one.</p>
        </div>
      </div>
    );
  }

  const lead = data.lead || {};
  const share = data.share || {};
  const hideContact = !!share.hide_contact || !!lead._contact_hidden;

  const infoFields = [
    { icon: User, label: "Full Name", value: lead.name, sensitive: true },
    { icon: Phone, label: "Phone", value: lead.phone, sensitive: true },
    { icon: Mail, label: "Email", value: lead.email, sensitive: true },
    { icon: MapPin, label: "Location", value: lead.location },
    { icon: Briefcase, label: "Case Type", value: lead.case_type },
    { icon: Tag, label: "Category", value: lead.case_category },
    { icon: User, label: "Parties Involved", value: lead.parties_involved },
    { icon: Calendar, label: "Incident Date", value: lead.incident_date },
    { icon: Clock, label: "Current Status", value: lead.current_status },
    { icon: FileText, label: "Documents (described)", value: lead.documents },
    { icon: MessageSquare, label: "Prior Consult", value: lead.prior_consultation },
    { icon: Phone, label: "Contact Pref.", value: lead.contact_preference, sensitive: true },
    { icon: Clock, label: "Availability", value: lead.availability },
    { icon: Shield, label: "Recommended Atty", value: lead.recommended_attorney_type },
    { icon: FileText, label: "Fee Preference", value: lead.fee_type },
    { icon: FileText, label: "Budget", value: lead.fee_budget },
  ];

  const sensitiveFields = infoFields.filter((f) => f.sensitive);
  const nonSensitive = infoFields.filter((f) => !f.sensitive && f.value);

  return (
    <div className="min-h-dvh bg-background" data-testid="shared-lead-page">
      {/* Header */}
      <header className="border-b border-border/40 bg-background/95 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Scale className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" style={{ fontFamily: "Manrope, sans-serif" }}>
                {hideContact ? "Confidential Client" : (lead.name || "Anonymous Client")}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {lead.case_category || lead.case_type || "—"} · {lead.location || "Location not provided"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/20 uppercase tracking-wider">
              <ShieldCheck className="w-3 h-3" /> Read-only
            </span>
            {hideContact && (
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 uppercase tracking-wider">
                <EyeOff className="w-3 h-3" /> Private
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8 w-full pb-12">
        {/* Banner */}
        <div className="mb-4 rounded-xl border border-primary/20 bg-primary/[0.04] p-3.5 flex items-start gap-3" data-testid="share-info-banner">
          <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-xs text-foreground/90 leading-relaxed">
            <strong>You&apos;re viewing a shared lead snapshot.</strong>{" "}
            {hideContact
              ? <>Contact details have been hidden by the sender. The case content below is visible read-only.</>
              : <>The full lead is visible. This is a read-only view — you can&apos;t edit or contact anyone from here.</>
            }
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-secondary/30 rounded-xl border border-border/30 w-fit mb-4 sm:mb-6 overflow-x-auto max-w-full">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${activeTab === t.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`share-tab-${t.id}`}
            >
              {t.label}
              {t.id === "transcript" && lead.conversation?.length > 0 && <span className="ml-1 text-muted-foreground">({lead.conversation.length})</span>}
              {t.id === "documents" && lead.uploaded_documents?.length > 0 && <span className="ml-1 text-muted-foreground">({lead.uploaded_documents.length})</span>}
            </button>
          ))}
        </div>

        {/* === Overview === */}
        {activeTab === "info" && (
          <div className="space-y-3 animate-fade-in" data-testid="share-info-section">
            {/* Contact group */}
            {hideContact ? (
              <PrivacyCurtain label="Contact info hidden by sender">
                {/* Placeholder noise (already redacted server-side, this is purely visual) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sensitiveFields.map(({ icon: Icon, label }) => (
                    <div key={label} className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-3.5 h-3.5 text-primary/60" />
                        <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">██████████████</p>
                    </div>
                  ))}
                </div>
              </PrivacyCurtain>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sensitiveFields.filter((f) => f.value).map(({ icon: Icon, label, value }) => (
                  <div key={label} className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="w-3.5 h-3.5 text-primary/60" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed break-words">{value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Non-sensitive group */}
            {nonSensitive.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {nonSensitive.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="w-3.5 h-3.5 text-primary/60" />
                      <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed break-words">{value}</p>
                  </div>
                ))}
              </div>
            )}

            {lead.fact_summary && (
              <div className="p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <FileText className="w-3.5 h-3.5 text-primary/60" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Fact Summary</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{lead.fact_summary}</p>
              </div>
            )}

            {(lead.desired_outcome || lead.case_analysis?.desired_outcome) && (
              <div className="p-3 sm:p-4 rounded-xl border border-primary/20 bg-primary/[0.04]">
                <div className="flex items-center gap-2 mb-1.5">
                  <Target className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs text-primary uppercase tracking-wider font-semibold">Desired Outcome</span>
                </div>
                <p className="text-sm text-foreground leading-relaxed">
                  {lead.desired_outcome || lead.case_analysis?.desired_outcome}
                </p>
              </div>
            )}
          </div>
        )}

        {/* === Case Score === */}
        {activeTab === "scoring" && (() => {
          const cs = lead.case_score || {};
          const overall = cs.overall_score;
          const ring = overall == null ? "text-muted-foreground border-border"
            : overall >= 70 ? "text-emerald-400 border-emerald-500/40"
            : overall >= 40 ? "text-amber-400 border-amber-500/40"
            : "text-red-400 border-red-500/40";
          const subColor = (n) => n == null ? "text-muted-foreground" : n >= 70 ? "text-emerald-400" : n >= 40 ? "text-amber-400" : "text-red-400";
          if (!lead.case_score) {
            return (
              <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl" data-testid="share-no-score">
                <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No case score generated for this lead yet.</p>
              </div>
            );
          }
          return (
            <div className="space-y-4 animate-fade-in" data-testid="share-scoring-section">
              <div className="p-5 rounded-2xl border border-border/30 bg-card/20 flex flex-col sm:flex-row items-center gap-5">
                <div className={`w-24 h-24 rounded-full border-4 ${ring} flex flex-col items-center justify-center flex-shrink-0`}>
                  <span className="text-3xl font-bold leading-none">{overall ?? "—"}</span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">/ 100</span>
                </div>
                <div className="flex-1 w-full">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {[
                      { label: "Win Prob.", v: cs.win_probability != null ? `${cs.win_probability}%` : "—", n: cs.win_probability },
                      { label: "Clarity", v: cs.clarity_score ?? "—", n: cs.clarity_score },
                      { label: "Urgency", v: cs.urgency_score ?? "—", n: cs.urgency_score },
                    ].map(({ label, v, n }) => (
                      <div key={label} className="text-center p-2 rounded-lg bg-secondary/20">
                        <div className={`text-lg font-bold ${subColor(n)}`}>{v}</div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                      </div>
                    ))}
                  </div>
                  {cs.rationale && <p className="text-xs text-muted-foreground italic leading-relaxed">{cs.rationale}</p>}
                </div>
              </div>
            </div>
          );
        })()}

        {/* === Legal analysis === */}
        {activeTab === "legal" && (
          <div className="animate-fade-in" data-testid="share-legal-section">
            {lead.legal_analysis ? (
              <LegalAnalysisDisplay analysis={lead.legal_analysis} />
            ) : (
              <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No legal analysis available for this lead.</p>
              </div>
            )}
          </div>
        )}

        {/* === Transcript === */}
        {activeTab === "transcript" && (
          <div className="space-y-3 animate-fade-in" data-testid="share-transcript-section">
            {hideContact && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] px-3 py-2 flex items-start gap-2">
                <EyeOff className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-200/90">Names, phone numbers, and email addresses have been redacted from the transcript.</p>
              </div>
            )}
            {(!lead.conversation || lead.conversation.length === 0) ? (
              <div className="text-center py-10 text-muted-foreground text-sm border border-border/30 rounded-xl">No transcript available</div>
            ) : (
              lead.conversation.map((msg, i) => (
                <div key={i} className={`flex gap-2 sm:gap-3 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`} data-testid={`share-transcript-msg-${i}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${msg.role === "assistant" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                    {msg.role === "assistant" ? "A" : "C"}
                  </div>
                  <div className={`max-w-[85%] flex flex-col gap-1 ${msg.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${msg.role === "user" ? "bg-primary/20 text-foreground rounded-tr-sm" : "bg-secondary/40 text-foreground rounded-tl-sm border border-border/20"}`}>
                      {msg.content}
                    </div>
                    <span className="text-xs text-muted-foreground/50 px-1">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* === Documents === */}
        {activeTab === "documents" && (
          <div className="space-y-2 animate-fade-in" data-testid="share-documents-section">
            <p className="text-xs text-muted-foreground mb-3">
              Document list only — files are not downloadable from a shared link.
            </p>
            {(!lead.uploaded_documents || lead.uploaded_documents.length === 0) ? (
              <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl">
                <Paperclip className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No documents uploaded</p>
              </div>
            ) : (
              lead.uploaded_documents.map((doc, i) => (
                <div key={doc.doc_id || i} className="flex items-center gap-3 p-3 sm:p-4 rounded-xl border border-border/30 bg-card/20">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Paperclip className="w-4 h-4 text-primary/70" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.original_filename || doc.filename || "Untitled document"}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(doc.size)}{doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString()}` : ""}
                    </p>
                  </div>
                  <Lock className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" title="Not downloadable" />
                </div>
              ))
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-border/20 text-center text-[11px] text-muted-foreground/70">
          ICantFindALawyer.com · Read-only shared lead view · This is not legal advice.
        </div>
      </div>
    </div>
  );
}
